import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar           from './components/Navbar';
import Footer           from './components/Footer';
import Home             from './pages/Home';
import About            from './pages/About';
import Services         from './pages/Services';
import Portfolio        from './pages/Portfolio';
import PortfolioDetails from './pages/PortfolioDetails';
import Pricing          from './pages/Pricing';
import Contact          from './pages/Contact';
import Terms            from './pages/Terms';
import FAQ              from './pages/FAQ';
import AdminApp         from './admin/AdminApp';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [pathname]);
  return null;
}

/* ── Floating WhatsApp Button — shows on every page ── */
const WHATSAPP_NUMBER = '918770329822';
const WHATSAPP_MSG    = encodeURIComponent(
  "Hi! I'm interested in your services. Can you please provide more details?"
);
const WHATSAPP_URL    = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MSG}`;

function WhatsAppFloat() {
  const [visible, setVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [pulse, setPulse] = useState(true);

  // Show after 1.5s, hide pulse after 6s
  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), 1500);
    const t2 = setTimeout(() => setPulse(false), 6000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <>
      <style>{`
        /* ── WhatsApp floating button ── */
        .wa-float {
          position: fixed;
          bottom: 28px;
          right: 28px;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 10px;
          pointer-events: none;
        }

        /* Tooltip bubble */
        .wa-tooltip {
          background: white;
          color: #111110;
          padding: 10px 16px;
          border-radius: 12px 12px 4px 12px;
          font-size: 0.82rem;
          font-weight: 600;
          box-shadow: 0 6px 28px rgba(0,0,0,0.15);
          white-space: nowrap;
          pointer-events: all;
          transition: opacity 0.25s ease, transform 0.25s ease;
          border: 1px solid #f0f0ee;
          position: relative;
        }
        .wa-tooltip::after {
          content: '';
          position: absolute;
          bottom: -6px; right: 16px;
          width: 12px; height: 12px;
          background: white;
          transform: rotate(45deg);
          border-right: 1px solid #f0f0ee;
          border-bottom: 1px solid #f0f0ee;
        }
        .wa-tooltip .wa-tt-title {
          font-weight: 700; color: #111110; margin-bottom: 2px;
          display: flex; align-items: center; gap: 6px;
        }
        .wa-tooltip .wa-tt-sub {
          color: #6e6e66; font-size: 0.75rem; font-weight: 400;
        }
        .wa-tooltip-close {
          position: absolute; top: 8px; right: 10px;
          background: none; border: none; cursor: pointer;
          color: #9ca3af; font-size: 0.9rem; line-height: 1;
          padding: 0;
        }

        /* Main button */
        .wa-btn {
          width: 58px; height: 58px;
          border-radius: 50%;
          background: #25D366;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; pointer-events: all;
          box-shadow: 0 4px 20px rgba(37,211,102,0.45);
          border: none;
          transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1),
                      box-shadow 0.25s ease;
          position: relative;
          text-decoration: none;
          animation: waEntrance 0.6s cubic-bezier(0.34,1.56,0.64,1) both;
        }
        .wa-btn:hover {
          transform: scale(1.12);
          box-shadow: 0 8px 28px rgba(37,211,102,0.55);
        }
        .wa-btn:active { transform: scale(0.95); }

        /* Entrance animation */
        @keyframes waEntrance {
          from { opacity: 0; transform: scale(0) translateY(20px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }

        /* Pulse rings */
        .wa-pulse-ring {
          position: absolute;
          width: 100%; height: 100%;
          border-radius: 50%;
          background: rgba(37,211,102,0.4);
          animation: waPulse 2s ease-out infinite;
          pointer-events: none;
        }
        .wa-pulse-ring:nth-child(2) { animation-delay: 0.7s; }

        @keyframes waPulse {
          0%   { transform: scale(1); opacity: 0.7; }
          100% { transform: scale(1.8); opacity: 0; }
        }

        /* Online indicator */
        .wa-online-dot {
          position: absolute;
          top: 2px; right: 2px;
          width: 14px; height: 14px;
          background: #4ade80;
          border-radius: 50%;
          border: 2.5px solid white;
          animation: onlinePulse 2.5s ease-in-out infinite;
        }
        @keyframes onlinePulse {
          0%,100% { opacity: 1; }
          50%     { opacity: 0.5; }
        }

        @media (max-width: 640px) {
          .wa-float { bottom: 18px; right: 18px; }
          .wa-btn { width: 52px; height: 52px; }
        }
      `}</style>

      {visible && (
        <div className="wa-float">
          {/* Tooltip */}
          {showTooltip && (
            <div className="wa-tooltip">
              <button className="wa-tooltip-close" onClick={() => setShowTooltip(false)}>✕</button>
              <div className="wa-tt-title">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Saanvi Innovations
              </div>
              <div className="wa-tt-sub">Typically replies within minutes</div>
            </div>
          )}

          {/* Main WhatsApp button */}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="wa-btn"
            aria-label="Chat with us on WhatsApp"
            onMouseEnter={() => setShowTooltip(true)}
          >
            {/* Pulse rings — shown for first 6 seconds */}
            {pulse && <><div className="wa-pulse-ring" /><div className="wa-pulse-ring" /></>}

            {/* Online dot */}
            <div className="wa-online-dot" />

            {/* WhatsApp SVG icon */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="30" height="30" fill="white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </a>
        </div>
      )}
    </>
  );
}

function PublicSite() {
  return (
    <div className="app">
      <Navbar />
      <main className="main-content">
        <Routes>
          <Route path="/"              element={<Home />} />
          <Route path="/about"         element={<About />} />
          <Route path="/services"      element={<Services />} />
          <Route path="/portfolio"     element={<Portfolio />} />
          <Route path="/portfolio/:id" element={<PortfolioDetails />} />
          <Route path="/pricing"       element={<Pricing />} />
          <Route path="/contact"       element={<Contact />} />
          <Route path="/terms"         element={<Terms />} />
          <Route path="/faq"           element={<FAQ />} />
        </Routes>
      </main>
      <Footer />
      {/* Floating WhatsApp — fixed position, always visible */}
      <WhatsAppFloat />
    </div>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        {/* Hidden admin — no Navbar/Footer */}
        <Route path="/admin/*" element={<AdminApp />} />
        {/* Public website */}
        <Route path="/*"       element={<PublicSite />} />
      </Routes>
    </Router>
  );
}

export default App;
